import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { DocumentsSuccessCodes } from '@app/translations/default-constants/toaster/success-codes/document.type';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  ButtonActionType,
  DocumentActionCategoryType,
  DocumentActionType,
  FileExt,
  UiZoneWidgetAttributeConfig,
  UploadFileType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import {
  DeleteTransactionReceipt,
  ReplaceTransactionReceipt,
  UpdateTransactionReceiptDetails,
  UploadTransactionReceipt,
} from '../../../actions/transactions.action';
import { TransactionStateType } from '../../../services/account-service/account.type';
import { ConfigService } from '../../../services/config-service/config-service.service';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { TransactionState } from '../../../state/transaction.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { createRedirectionUrlFromNavigationOptions } from '../../utils';
import { handleFileUpload } from '../document-actions';
import { showUploadFileConfirmDialog } from '../upload-confirm-dialog.utils';
import { ConfirmDialogService } from '@app/finxone-web-frontend/app/Modules/shared/components-library/confirmation-dilog/confirmation-dialog.service';
import { uploadFileViaFlutterWebView } from '@app/finxone-web-frontend/app/lib/utils/upload-file-utils/flutter-file-upload.utils';

export function deleteTransactionReceipt(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const navigationConfig = attri.widgetProperties?.['actionNavigationConfigurations'];
  const router = injector.get(Router);
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const type = attri.widgetProperties?.documentActionType ?? DocumentActionType.TRANSACTIONS;
  const currentTransactionData = store.selectSnapshot(TransactionState.getTransactions);
  const documentId = currentTransactionData.transactionReceiptDetails?.[0]?.fileId ?? '';

  // Navigate to Processing
  if (navigationConfig?.[ActionButtonOutcomesType.PROCESSING]) {
    const redirectUrl = createRedirectionUrlFromNavigationOptions(
      navigationConfig[ActionButtonOutcomesType.PROCESSING],
    );
    router.navigateByUrl(redirectUrl);
  }

  // Dispatch the delete API call
  store
    .dispatch(new DeleteTransactionReceipt(type, currentTransactionData?.currentTransactionId, documentId))
    .subscribe({
      next: () => {
        alertHandlerService.showAlertFn('success', 'File Deleted successfully!');
        // Navigation to success
        if (navigationConfig?.[ActionButtonOutcomesType.SUCCESS]) {
          const redirectUrl = createRedirectionUrlFromNavigationOptions(
            navigationConfig[ActionButtonOutcomesType.SUCCESS],
          );
          router.navigateByUrl(redirectUrl);
        }
      },
      error: () => {
        // Navigation to failed
        if (navigationConfig?.[ActionButtonOutcomesType.FAILURE]) {
          const redirectUrl = createRedirectionUrlFromNavigationOptions(
            navigationConfig[ActionButtonOutcomesType.FAILURE],
          );
          router.navigateByUrl(redirectUrl);
        }
      },
    });
}

export async function uploadTransactionReceipt(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const flutterWebView = (window as any).flutter_inappwebview;
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const router = injector.get(Router);
  const type = attri.widgetProperties?.documentActionType ?? DocumentActionType.TRANSACTIONS;
  const documentType = attri.widgetProperties?.['documentType'] ?? UploadFileType.IMAGE;
  const currentTransactionData = store.selectSnapshot(TransactionState.getTransactions);
  const buttonActionType = attri?.widgetProperties?.['buttonActionType'] as ButtonActionType;
  const fileTypeMap: { [key: string]: string[] } = {
    [UploadFileType.IMAGE]: [FileExt.JPEG, FileExt.JPG, FileExt.SVG, FileExt.PNG, FileExt.GIF, FileExt.WEBP],
  };
  const flutterRequestData = {
    fileType: fileTypeMap[documentType],
    maxFileSize: 20971520,
    usePhotoGallery: true,
    maxFile: 1,
    type: type,
    entityId: currentTransactionData.currentTransactionId,
    actionType: buttonActionType,
    category: DocumentActionCategoryType.RECEIPTS,
  };

  const confirmDialogService = injector.get(ConfirmDialogService);

  if (flutterWebView) {
    await uploadFileViaFlutterWebView(
      flutterWebView,
      flutterRequestData,
      confirmDialogService,
      store,
      router,
      alertHandlerService,
      (flutterResponse) => {
        store.dispatch(new UpdateTransactionReceiptDetails(flutterResponse));
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          DocumentsSuccessCodes.DOCUMENT_UPLOAD_SUCCESS,
        ]);
      },
    );
    return;
  }

  uploadDocumentViaWeb(injector, type, currentTransactionData, store, documentType, buttonActionType, router);
}

function uploadDocumentViaWeb(
  injector: Injector,
  type: DocumentActionType,
  currentTransactionData: TransactionStateType,
  store: Store,
  documentType: UploadFileType,
  buttonActionType: ButtonActionType,
  router: Router,
) {
  const alertHandlerService = injector.get(AlertHandlerService);
  const confirmDialogService = injector.get(ConfirmDialogService);

  let fileType = '';
  if (documentType === UploadFileType.IMAGE) {
    fileType = '.jpeg, .jpg, .png, .svg, .gif, .webp';
  }

  handleFileUpload(fileType, (file) => {
    if (!file) return;

    showUploadFileConfirmDialog(null, confirmDialogService, file.name).then((result: boolean) => {
      if (!result) return;

      const formData = new FormData();
      formData.append('files', file);
      formData.append('entityId', currentTransactionData?.currentTransactionId);
      formData.append('type', type);
      formData.append('category', DocumentActionCategoryType.RECEIPTS);

      uploadTransactionDocument(
        formData,
        store,
        alertHandlerService,
        buttonActionType,
        type,
        currentTransactionData?.currentTransactionId,
        router,
      );
    });
  });
}

function uploadTransactionDocument(
  formData: FormData,
  store: Store,
  alertHandlerService: AlertHandlerService,
  buttonActionType: ButtonActionType,
  type: DocumentActionType,
  currentTransactionId: string,
  router: Router,
) {
  switch (buttonActionType) {
    case ButtonActionType.UPLOAD_DOCUMENT:
      store.dispatch(new UploadTransactionReceipt(formData)).subscribe({
        next: () => {
          findAndShowAlertFromCode(store, router, alertHandlerService, [
            DocumentsSuccessCodes.DOCUMENT_UPLOAD_SUCCESS,
          ]);
        },
        error: (err) => {
          console.error('Error', err);
        },
      });
      break;

    case ButtonActionType.REPLACE_DOCUMENT:
      store.dispatch(new ReplaceTransactionReceipt(formData, type, currentTransactionId)).subscribe({
        next: () => {
          findAndShowAlertFromCode(store, router, alertHandlerService, [
            DocumentsSuccessCodes.DOCUMENT_UPLOAD_SUCCESS,
          ]);
        },
        error: (err) => {
          console.error('Error', err);
        },
      });
      break;

    default:
      console.warn(`Unhandled button action type: ${buttonActionType}`);
      break;
  }
}

/*
 * If a fileId exists in the transaction receipt details, the button will be shown (hide = false),
 * otherwise, it will be hidden (hide = true).
 */
export function transactionViewDocumentHide(
  store: Store,
  ctaButtonSignalService: CtaButtonSignalService,
  attri: UiZoneWidgetAttributeConfig,
  configService: ConfigService,
) {
  const isWorkflow = configService.getActiveConfigType().isWorkflow;
  if (!isWorkflow) {
    const key = attri?.['buttonActionType'] as ButtonActionType;
    const data$ = store.select(TransactionState.getTransactions);
    return data$.subscribe((res) => {
      const fileId = res.transactionReceiptDetails?.[0]?.fileId;
      ctaButtonSignalService.setHideButtonSignal({
        [key]: !fileId,
      });
    });
  }
  return;
}

/*
 * If a fileId exists in the transaction receipt details, the button will be hidden (hide = true),
 * otherwise, it will be shown (hide = false).
 */
export function transactionUploadDocumentHide(
  store: Store,
  ctaButtonSignalService: CtaButtonSignalService,
  attri: UiZoneWidgetAttributeConfig,
  configService: ConfigService,
) {
  const isWorkflow = configService.getActiveConfigType().isWorkflow;
  if (!isWorkflow) {
    const key = attri?.['buttonActionType'] as string;
    const data$ = store.select(TransactionState.getTransactions);
    return data$.subscribe((res) => {
      const fileId = res.transactionReceiptDetails?.[0]?.fileId;
      ctaButtonSignalService.setHideButtonSignal({
        [key]: !!fileId,
      });
    });
  }
  return;
}
